import styled from '@emotion/styled';
import * as React from 'react';
import { BREAKPOINT, PAGE_LAYOUT_GAP } from '../../vars';

export interface ReadingLayoutWidgetProps {
  children: React.ReactNode;
  navigation?: React.ReactNode;
  tableOfContents?: React.ReactNode;
  mobileSidebar?: 'hide' | 'stack';
  sidebarWidth: number;
  compactSidebarWidth?: number;
  contentWidth: number;
}

export const ReadingLayoutWidget: React.FC<ReadingLayoutWidgetProps> = ({
  children,
  navigation,
  tableOfContents,
  mobileSidebar = 'hide',
  sidebarWidth,
  compactSidebarWidth = sidebarWidth,
  contentWidth
}) => (
  <S.Layout
    $hasRightSidebar={!!navigation && !!tableOfContents}
    $hasSidebar={!!navigation || !!tableOfContents}
    $sidebarWidth={sidebarWidth}
    $compactSidebarWidth={compactSidebarWidth}
    $contentWidth={contentWidth}
  >
    {(navigation || tableOfContents) && (
      <S.LeftSidebar $mobileSidebar={mobileSidebar}>{navigation ?? tableOfContents}</S.LeftSidebar>
    )}
    <S.Content>{children}</S.Content>
    {navigation && tableOfContents && <S.RightSidebar>{tableOfContents}</S.RightSidebar>}
  </S.Layout>
);

namespace S {
  interface LayoutProps {
    $hasRightSidebar: boolean;
    $hasSidebar: boolean;
    $sidebarWidth: number;
    $compactSidebarWidth: number;
    $contentWidth: number;
  }

  export const Layout = styled.div<LayoutProps>`
    display: grid;
    grid-template-columns: ${(p) =>
      p.$hasSidebar
        ? `${p.$sidebarWidth}px minmax(0, ${p.$contentWidth}px)${p.$hasRightSidebar ? ` ${p.$sidebarWidth}px` : ''}`
        : `minmax(0, ${p.$contentWidth}px)`};
    gap: ${PAGE_LAYOUT_GAP}px;
    justify-content: center;
    align-items: start;

    @media (max-width: ${BREAKPOINT.DOCS_SIDEBARS}px) {
      grid-template-columns: ${(p) =>
        p.$hasSidebar
          ? `${p.$compactSidebarWidth}px minmax(0, ${p.$contentWidth}px)`
          : `minmax(0, ${p.$contentWidth}px)`};
    }

    @media (max-width: ${BREAKPOINT.IPAD}px) {
      grid-template-columns: minmax(0, ${(p) => p.$contentWidth}px);
    }
  `;

  export const LeftSidebar = styled.div<{ $mobileSidebar: 'hide' | 'stack' }>`
    display: contents;

    @media (max-width: ${BREAKPOINT.IPAD}px) {
      display: ${(p) => (p.$mobileSidebar === 'stack' ? 'contents' : 'none')};
    }
  `;

  export const RightSidebar = styled.div`
    display: contents;

    @media (max-width: ${BREAKPOINT.DOCS_SIDEBARS}px) {
      display: none;
    }
  `;

  export const Content = styled.div`
    min-width: 0;
  `;
}
